import React, { useEffect, useId, useRef, useState } from 'react';
import { FaChevronDown, FaSearch, FaTimes } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { resolveMediaUrl } from '../../../hooks/useStreamBootstrap.js';
import { useCrate } from '../../../hooks/useCrate.js';
import { useCrateFacets } from '../../../hooks/useCrateFacets.js';
import SongRequestSheet from './SongRequestSheet.jsx';
import VinylSleeve, { coverLabel } from '../VinylSleeve.jsx';
import FavoriteButton from '../FavoriteButton.jsx';
import { useFavorites } from '../../../hooks/useFavorites.js';
import { isSignedIn } from '../../../_helpers/safeRedirect.js';
import '../../../styles/SongCratePanel.css';
import '../../../styles/StreamCta.css';

/** The crate's filters (mockups 20–21, ENG-705/707): each opens a chip row of
 * the values its facet offers; several values of one filter widen it, values
 * of different filters narrow it. */
const FILTERS = [
  { key: 'decades', facet: 'decades', label: 'stream.crate.panel.decade', valueOf: (f) => Number(f.value) },
  { key: 'genreIds', facet: 'genres', label: 'stream.crate.panel.genre', valueOf: (f) => f.id },
  { key: 'moods', facet: 'moods', label: 'stream.crate.panel.vibe', valueOf: (f) => f.value },
  { key: 'tagIds', facet: 'tags', label: 'stream.crate.panel.tags', valueOf: (f) => f.id },
];

const NO_FILTERS = { decades: [], genreIds: [], moods: [], tagIds: [] };

const SORTS = ['most_requested', 'new'];
const SORT_KEYS = { most_requested: 'stream.crate.panel.sort.mostRequested', new: 'stream.crate.panel.sort.new' };

/**
 * "SONG CRATE" (mockup 16, ENG-697): the band's requestable songs in place
 * of the stream page's right column, with search, filters and an endless
 * artwork grid (`GET /public/bands/{id}/songs`, ENG-698). Each filter opens a
 * chip row of only the values the crate has (`/songs/facets`, mockup 20,
 * ENG-705); applied values show as chips with × and the count reads "N OF
 * TOTAL SONGS" (mockup 21, ENG-707).
 *
 * A song played tonight is labelled; when the show refuses repeats it is
 * dimmed and can't be picked. Picking a song opens its song page
 * (`onOpenSong`, mockup 22, ENG-709); with `switchEntryId`, picking asks to
 * confirm the switch of that request here. The queue has the last word: what
 * it refuses is shown on the confirm sheet.
 *
 * `playingSongId` is the song on stage; when it changes the played-tonight
 * flags are read again.
 */
const SongCratePanel = ({ bandId, switchEntryId = null, playingSongId = null, onClose, onDone, onSignIn, onOpenSong }) => {
  const { t, format } = useLocale();
  const headingId = useId();
  const [search, setSearch] = useState('');
  // Applied values per filter, each { value, label } (the chips with ×).
  const [applied, setApplied] = useState(NO_FILTERS);
  const [openFilter, setOpenFilter] = useState(null);
  const [sort, setSort] = useState('');
  const facets = useCrateFacets(bandId);
  const crate = useCrate(bandId, {
    search,
    sort,
    ...Object.fromEntries(FILTERS.map(({ key }) => [key, applied[key].map((a) => a.value)])),
  });
  const { refreshFlags } = crate;

  const [picked, setPicked] = useState(null);

  // Each tile's favorite star (ENG-443): the same favorites as the song list,
  // toggled in place without reordering the crate. Signed out, the star goes
  // to sign-in like a request does.
  const favorites = useFavorites(bandId);
  const { toggle: toggleFavorite } = favorites;
  const favorite = async (song) => {
    if (await toggleFavorite(song.id) === 'signin') onSignIn?.();
  };

  const firstPlaying = useRef(true);
  useEffect(() => {
    if (firstPlaying.current) {
      firstPlaying.current = false;
      return;
    }
    refreshFlags();
  }, [playingSongId, refreshFlags]);

  // Endless scroll: the sentinel under the grid loads the next page as it
  // comes into the panel's own scroll area.
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadMoreRef = useRef(crate.loadMore);
  loadMoreRef.current = crate.loadMore;
  const canObserve = typeof window !== 'undefined' && 'IntersectionObserver' in window;
  useEffect(() => {
    if (!canObserve || !sentinelRef.current) return undefined;
    const observer = new window.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMoreRef.current();
    }, { root: scrollRef.current, rootMargin: '200px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [canObserve]);

  const pick = (song) => {
    if (song.repeatBlocked) return;
    // A request opens the song page (mockup 22, ENG-709); a switch, which
    // costs nothing, is confirmed here.
    if (!switchEntryId && onOpenSong) {
      onOpenSong(song);
      return;
    }
    // A request needs a session: signed out, browsing is fine but picking
    // goes to sign-in (ENG-503).
    if (!isSignedIn()) {
      onSignIn?.();
      return;
    }
    setPicked(song);
  };

  const labelOf = (filter, f) => (filter.key === 'decades'
    ? t('stream.crate.panel.decadeOption', { decade: String(f.value).slice(2) })
    : String(f.value));
  const isApplied = (key, value) => applied[key].some((a) => a.value === value);
  const toggle = (filter, f) => {
    const value = filter.valueOf(f);
    setApplied((prev) => ({
      ...prev,
      [filter.key]: isApplied(filter.key, value)
        ? prev[filter.key].filter((a) => a.value !== value)
        : [...prev[filter.key], { value, label: labelOf(filter, f) }],
    }));
  };
  const remove = (key, value) => setApplied((prev) => ({ ...prev, [key]: prev[key].filter((a) => a.value !== value) }));
  const chips = FILTERS.flatMap(({ key }) => applied[key].map((a) => ({ key, ...a })));
  const offered = FILTERS.filter((filter) => facets[filter.facet].length > 0);
  const open = offered.find((filter) => filter.key === openFilter) ?? null;

  const filtered = Boolean(search.trim() || chips.length);

  return (
    <section
      className="crate-panel"
      aria-labelledby={headingId}
      data-testid="crate-panel"
      onKeyDown={(e) => {
        // Esc closes an open chip row (mockup 20), not the panel.
        if (e.key === 'Escape' && openFilter) {
          e.stopPropagation();
          setOpenFilter(null);
        }
      }}
    >
      <header className="crate-panel-header">
        <div>
          <span className="crate-panel-kicker">{t(switchEntryId ? 'stream.crate.panel.switchKicker' : 'stream.crate.panel.kicker')}</span>
          <h2 id={headingId} className="crate-panel-title">
            {filtered
              ? t('stream.crate.panel.countOf', { count: crate.crateTotal, formattedCount: format.number(crate.total), formattedTotal: format.number(crate.crateTotal) })
              : t('stream.crate.panel.count', { count: crate.total, formattedCount: format.number(crate.total) })}
          </h2>
        </div>
        <button type="button" className="crate-panel-close" onClick={onClose} aria-label={t('stream.crate.panel.close')}>
          <FaTimes aria-hidden="true" />
        </button>
      </header>

      <label className="crate-panel-search">
        <FaSearch aria-hidden="true" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('stream.crate.panel.searchPlaceholder')}
          aria-label={t('stream.crate.panel.searchLabel')}
          data-testid="crate-search"
        />
      </label>

      <div className="crate-panel-filters">
        {offered.map((filter) => (
          <button
            key={filter.key}
            type="button"
            className={`crate-panel-chip crate-panel-filter${applied[filter.key].length ? ' crate-panel-chip--on' : ''}`}
            aria-expanded={openFilter === filter.key}
            aria-controls={`${headingId}-${filter.key}`}
            onClick={() => setOpenFilter((current) => (current === filter.key ? null : filter.key))}
            data-testid={`crate-filter-${filter.key}`}
          >
            {t(filter.label)}
            <FaChevronDown aria-hidden="true" />
          </button>
        ))}
        {SORTS.map((s) => (
          <button
            key={s}
            type="button"
            className={`crate-panel-chip${sort === s ? ' crate-panel-chip--on' : ''}`}
            aria-pressed={sort === s}
            onClick={() => setSort((current) => (current === s ? '' : s))}
          >
            {t(SORT_KEYS[s])}
          </button>
        ))}
      </div>

      {open && (
        <div
          id={`${headingId}-${open.key}`}
          className="crate-panel-values"
          role="group"
          aria-label={t(open.label)}
          data-testid="crate-values"
        >
          {facets[open.facet].map((f) => {
            const value = open.valueOf(f);
            return (
              <button
                key={String(value)}
                type="button"
                className={`crate-panel-chip${isApplied(open.key, value) ? ' crate-panel-chip--on' : ''}`}
                aria-pressed={isApplied(open.key, value)}
                onClick={() => toggle(open, f)}
              >
                {labelOf(open, f)}
              </button>
            );
          })}
        </div>
      )}

      {chips.length > 0 && (
        <ul className="crate-panel-applied" aria-label={t('stream.crate.panel.applied')}>
          {chips.map((chip) => (
            <li key={`${chip.key}-${chip.value}`}>
              <span className="crate-panel-applied-chip">
                {chip.label}
                <button
                  type="button"
                  onClick={() => remove(chip.key, chip.value)}
                  aria-label={t('stream.crate.panel.removeFilter', { label: chip.label })}
                >
                  <FaTimes aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="crate-panel-scroll" ref={scrollRef}>
        {favorites.error && (
          <p className="crate-panel-favorite-error" role="alert" data-testid="crate-favorite-error">{t('favorites.failed')}</p>
        )}
        {crate.songs.length === 0 && !crate.loading && !crate.error && (
          <p className="crate-panel-empty" data-testid="crate-empty">
            {t(filtered ? 'stream.crate.panel.noMatches' : 'stream.crate.panel.emptyCrate')}
          </p>
        )}
        <ul className="crate-panel-grid">
          {crate.songs.map((song) => {
            const art = resolveMediaUrl(song.artworkUrl);
            return (
              <li key={song.id}>
                <button
                  type="button"
                  className={`crate-tile vinyl-sleeve-trigger vinyl-sleeve-trigger--focus${song.repeatBlocked ? ' crate-tile--blocked' : ''}${song.playedTonight ? ' crate-tile--played' : ''}`}
                  onClick={() => pick(song)}
                  disabled={song.repeatBlocked}
                  data-testid={`crate-tile-${song.id}`}
                >
                  <VinylSleeve
                    artworkUrl={art}
                    fallbackLabel={coverLabel(song.title, t('publicSongList.card.coverFallback'))}
                    className="crate-tile-sleeve"
                    coverClassName="crate-tile-art"
                    testId={`crate-sleeve-${song.id}`}
                    lazy
                  >
                    {!song.repeatBlocked && <span className="crate-tile-request stream-cta" aria-hidden="true">{t('stream.crate.panel.request')}</span>}
                    {song.playedTonight && <span className="crate-tile-played">{t('stream.crate.panel.playedTonight')}</span>}
                  </VinylSleeve>
                  <span className="crate-tile-title">{song.title}</span>
                  <span className="crate-tile-artist">{song.artist}</span>
                </button>
                <FavoriteButton
                  className="crate-tile-favorite"
                  title={song.title}
                  favorited={favorites.isFavorite(song.id)}
                  busy={favorites.isPending(song.id)}
                  onToggle={() => favorite(song)}
                  testId={`crate-favorite-${song.id}`}
                />
              </li>
            );
          })}
        </ul>
        {crate.loading && <p className="crate-panel-status" role="status">{t('stream.crate.panel.loading')}</p>}
        {crate.error && (
          <p className="crate-panel-status" role="alert">
            {t('stream.crate.panel.loadFailed')}{' '}
            <button type="button" className="crate-panel-link" onClick={crate.retry}>{t('stream.crate.panel.retry')}</button>
          </p>
        )}
        {crate.hasMore && !crate.loading && !crate.error && !canObserve && (
          <button type="button" className="crate-panel-more" onClick={crate.loadMore}>{t('stream.crate.panel.loadMore')}</button>
        )}
        <div ref={sentinelRef} className="crate-panel-sentinel" aria-hidden="true" />
      </div>

      {picked && (
        <SongRequestSheet
          key={picked.id}
          bandId={bandId}
          song={picked}
          switchEntryId={switchEntryId}
          onCancel={() => setPicked(null)}
          onDone={onDone}
        />
      )}
    </section>
  );
};

export default SongCratePanel;
